import type { MutableRefObject } from "react";
import { useQuery } from "react-query";
import { catalogApi, type CatalogPageFilters } from "@/lib/api/catalog";

type ResourceType = "all" | "physical" | "digital";

export type CatalogUnifiedSearchParams = {
  tenantId: string | undefined;
  /** Falsy/undefined prevents the query from running (e.g. permission guard may be `boolean | undefined`). */
  enabled?: boolean;
  debouncedQuery: string;
  resourceType: ResourceType;
  selectedCollection: string;
  selectedItemType: string;
  selectedBranch: string;
  page: number;
  pageSize: number;
  /** Key = page number (2+), value = cursor token for that page (from previous response). */
  pageCursors: Record<number, string>;
  initialLoadDoneRef: MutableRefObject<boolean>;
};

/**
 * Server state for admin catalog grid. Uses keepPreviousData to avoid blanking the list on refetch.
 */
export function useCatalogUnifiedSearchQuery(p: CatalogUnifiedSearchParams) {
  const filters: Record<string, string> = {};
  if (p.selectedCollection !== "all") filters.collectionId = p.selectedCollection;
  if (p.selectedItemType !== "all") filters.itemTypeId = p.selectedItemType;
  if (p.selectedBranch !== "all") filters.branchId = p.selectedBranch;

  const cursor = p.page > 1 && p.resourceType !== "all" ? p.pageCursors[p.page] : undefined;

  const needsCursor = p.resourceType !== "all" && p.page > 1;
  const queryEnabled = Boolean(p.enabled && p.tenantId) && (!needsCursor || Boolean(cursor));

  return useQuery(
    [
      "catalog",
      "unified",
      p.tenantId,
      p.debouncedQuery,
      p.resourceType,
      p.selectedCollection,
      p.selectedItemType,
      p.selectedBranch,
      p.page,
      p.pageSize,
      cursor ?? "",
    ],
    ({ signal }) => {
      const includeBundle = !p.initialLoadDoneRef.current;
      return catalogApi.unifiedSearch(
        p.debouncedQuery || "",
        p.resourceType === "all" ? undefined : p.resourceType,
        p.page,
        p.pageSize,
        filters,
        {
          signal,
          cursor,
          includeStats: includeBundle,
          includeFilters: includeBundle,
        }
      );
    },
    {
      enabled: queryEnabled,
      keepPreviousData: true,
      staleTime: 60_000,
    }
  );
}
